<template>
	<div
		class="min-h-screen p-4 motor-commercial-flow bg-gray-50 dark:bg-gray-900 md:p-8"
	>
		<div class="mx-auto max-w-7xl">
			<header class="mb-8 text-center">
				<h1
					class="mb-2 text-3xl font-bold text-gray-900 md:text-4xl dark:text-gray-100"
				>
					Motor Commercial Insurance
				</h1>
				<p class="text-gray-600 dark:text-gray-400">
					Get a comprehensive quote in just a few steps.
				</p>
			</header>

			<div class="grid gap-6 lg:grid-cols-3">
				<div class="hidden lg:block lg:col-span-1">
					<div
						class="p-6 bg-white shadow-sm dark:bg-gray-800 rounded-2xl"
					>
						<h2
							class="mb-4 text-xl font-semibold text-gray-900 dark:text-gray-100"
						>
							Your Progress
						</h2>
						<ol class="relative space-y-6">
							<li
								v-for="(step, index) in steps"
								:key="index"
								class="flex items-start"
							>
								<div
									class="relative flex items-center flex-shrink-0"
								>
									<div
										class="flex items-center justify-center w-10 h-10 text-lg font-bold text-white rounded-full"
										:class="{
											'bg-blue-600':
												index + 1 === currentStep,
											'bg-green-500':
												index + 1 < currentStep,
											'bg-gray-400':
												index + 1 > currentStep,
										}"
									>
										<span v-if="index + 1 < currentStep">
											<svg
												xmlns="http://www.w3.org/2000/svg"
												class="w-6 h-6"
												viewBox="0 0 24 24"
												fill="currentColor"
											>
												<path
													d="M9.9997 15.1709L6.1094 11.2807L4.6953 12.6948L9.9997 17.9992L19.9997 8.00007L18.5856 6.58594L9.9997 15.1709Z"
												></path>
											</svg>
										</span>
										<span v-else>{{ index + 1 }}</span>
									</div>
									<span
										v-if="index < steps.length - 1"
										class="absolute w-0.5 h-full transform -translate-x-1/2 rounded-full left-1/2 top-10"
										:class="{
											'bg-blue-300':
												index + 1 < currentStep,
											'bg-gray-300':
												index + 1 >= currentStep,
										}"
									></span>
								</div>
								<div class="ml-4">
									<p
										class="text-lg font-semibold"
										:class="{
											'text-gray-900 dark:text-gray-100':
												index + 1 === currentStep,
											'text-gray-600 dark:text-gray-300':
												index + 1 !== currentStep,
										}"
									>
										{{ step.title }}
									</p>
									<p
										v-if="step.description"
										class="text-sm text-gray-500 dark:text-gray-400"
									>
										{{ step.description }}
									</p>
								</div>
							</li>
						</ol>
					</div>
				</div>

				<div class="lg:col-span-2">
					<div class="flex flex-col h-full">
						<component
							:is="currentStepComponent"
							:vehicle-type="vehicleType"
							:vehicle-value="vehicleValue"
							:tonnage-capacity="tonnageCapacity"
							:cover-type="coverType"
							:usage-details="usageDetails"
							:products="quotedProducts"
							:selected-product="selectedProduct"
							:vehicle-details="vehicleDetails"
							:kyc-details="kycDetails"
							:payment-status="paymentStatus"
							@next="handleNext"
							@previous="handlePrevious"
						/>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
// Import all step components
import CommercialCoverTypeSelection from './components/CommercialCoverTypeSelection.vue';
import CommercialVehicleValuation from './components/CommercialVehicleValuation.vue';
import CommercialProductQuotation from './components/CommercialProductQuotation.vue';
import CommercialVehicleDetailsForm from './components/CommercialVehicleDetailsForm.vue';
import KYCDetailsForm from './components/KYCDetailsForm.vue';
import CommercialSummaryPage from './components/CommercialSummaryPage.vue';
import PaymentPage from './components/PaymentConfirmation.vue';
import { mapState, mapActions } from 'vuex';

export default {
	name: 'MotorCommercialFlow',
	components: {
		CommercialCoverTypeSelection,
		CommercialVehicleValuation,
		CommercialProductQuotation,
		CommercialVehicleDetailsForm,
		KYCDetailsForm,
		CommercialSummaryPage,
		PaymentPage,
	},
	data() {
		return {
			steps: [
				{
					id: 1,
					title: 'Cover Type',
					component: CommercialCoverTypeSelection,
					description: 'Choose your desired insurance coverage',
				},
				{
					id: 2,
					title: 'Valuation',
					component: CommercialVehicleValuation,
					description: "Provide your vehicle's value",
				},
				{
					id: 3,
					title: 'Get Quote',
					component: CommercialProductQuotation,
					description: 'Review and select from available quotations',
				},
				{
					id: 4,
					title: 'Vehicle Details',
					component: CommercialVehicleDetailsForm,
					description: 'Provide specific details about your vehicle',
				},
				{
					id: 5,
					title: 'Your Details',
					component: KYCDetailsForm,
					description: 'Enter your personal information (KYC)',
				},
				{
					id: 6,
					title: 'Summary',
					component: CommercialSummaryPage,
					description: 'Confirm all details before payment',
				},
				{
					id: 7,
					title: 'Payment',
					component: PaymentPage,
					description: 'Finalize your insurance policy',
				},
			],
		};
	},
	computed: {
		...mapState('motorCommercial', [
			'currentStep',
			'vehicleType',
			'vehicleValue',
			'tonnageCapacity',
			'coverType',
			'usageDetails',
			'quotedProducts',
			'selectedProduct',
			'vehicleDetails',
			'kycDetails',
			'paymentStatus',
		]),
		currentStepComponent() {
			const step = this.steps.find((s) => s.id === this.currentStep);
			return step ? step.component : null;
		},
	},
	methods: {
		...mapActions('motorCommercial', [
			'nextStep',
			'previousStep',
			'fetchQuotations',
			'setCoverType',
			'setVehicleValue',
			'setSelectedProduct',
			'setVehicleDetails',
			'setKYCDetails',
			'setPaymentStatus',
		]),
		handleNext(data) {
			// Logic to handle data from child components and advance the step
			if (data && data.coverType) {
				this.setCoverType(data.coverType).then(() => {
					this.nextStep();
					this.fetchQuotations(); // Fetch quotes after cover type is set
				});
			} else if (data && data.vehicleValue) {
				this.setVehicleValue(data.vehicleValue).then(() =>
					this.nextStep(),
				);
			} else if (data && data.selectedProduct) {
				this.setSelectedProduct(data.selectedProduct).then(() =>
					this.nextStep(),
				);
			} else if (data && data.vehicleDetails) {
				this.setVehicleDetails(data.vehicleDetails).then(() =>
					this.nextStep(),
				);
			} else if (data && data.kycDetails) {
				this.setKYCDetails(data.kycDetails).then(() => this.nextStep());
			} else if (data && data.paymentStatus) {
				this.setPaymentStatus(data.paymentStatus).then(() =>
					this.nextStep(),
				);
			} else {
				this.nextStep();
			}
		},
		handlePrevious() {
			this.previousStep();
		},
	},
	watch: {
		// This watcher can be used to automatically trigger actions when state changes,
		// like fetching quotations when coverType and vehicleValue are ready.
		coverType(newVal, oldVal) {
			if (newVal && this.vehicleValue) {
				this.fetchQuotations();
			}
		},
		vehicleValue(newVal, oldVal) {
			if (newVal && this.coverType) {
				this.fetchQuotations();
			}
		},
	},
};
</script>

<style scoped>
/* Your existing styles remain the same */
.motor-commercial-flow {
	font-family: 'Inter', sans-serif;
}
.animate-fade-in-up {
	animation: fade-in-up 0.8s ease-out forwards;
}
@keyframes fade-in-up {
	from {
		opacity: 0;
		transform: translateY(20px);
	}
	to {
		opacity: 1;
		transform: translateY(0);
	}
}
</style>
